import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { getAuth, sendPasswordResetEmail } from 'firebase/auth';
import { app } from '../firebase';

const ForgotPassword: React.FC = () => {
    const [email, setEmail] = useState('');
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);
    const [loading, setLoading] = useState(false);
    const auth = getAuth(app);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setLoading(true);
        try {
            // Configure custom email template with your domain
            const actionCodeSettings = {
                url: `${window.location.origin}/reset-password`,
                handleCodeInApp: true,
            };

            await sendPasswordResetEmail(auth, email, actionCodeSettings);
            setSuccess(true);
        } catch (err: any) {
            setError(err.message);
        }
        setLoading(false);
    };

    return (
        <div className="min-h-screen bg-gray-900 flex flex-col">
            {/* Header */}
            <header className="bg-gray-800 py-4">
                <div className="container mx-auto px-4 sm:px-6 flex flex-col sm:flex-row items-center gap-3">
                    <img src="/images/logo.png" alt="Fixisy Logo" className="h-8 w-8 sm:h-10 sm:w-10 object-contain" />
                    <Link to="/" className="text-xl sm:text-2xl font-bold text-cyan-400">Fixisy</Link>
                </div>
            </header>

            {/* Main Content */}
            <main className="flex-1 flex flex-col items-center justify-center px-4 py-8">
                <div className="w-full max-w-xs sm:max-w-md">
                    <div className="mb-6 sm:mb-8 text-center">
                        <h1 className="text-2xl sm:text-3xl font-bold text-white mb-2">Reset Password</h1>
                        <p className="text-gray-400 text-sm sm:text-base">
                            Enter your email address and we'll send you instructions to reset your password.
                        </p>
                    </div>

                    {/* Success message */}
                    {success ? (
                        <div className="text-center">
                            <div className="mb-4 text-green-400 text-sm sm:text-base">
                                Password reset instructions have been sent to your email.
                                Please check your inbox and follow the instructions.
                            </div>
                            <Link
                                to="/signin"
                                className="text-cyan-400 hover:text-cyan-300 text-sm sm:text-base"
                            >
                                Return to Sign In
                            </Link>
                        </div>
                    ) : (
                        <>
                            {/* Error message */}
                            {error && (
                                <div className="mb-4 text-red-400 text-center text-sm">
                                    {error}
                                </div>
                            )}

                            {/* Reset Form */}
                            <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
                                <div>
                                    <input
                                        type="email"
                                        placeholder="Email"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        className="w-full px-4 py-3 rounded-lg bg-gray-800 text-white border border-gray-700 focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 text-sm sm:text-base"
                                        required
                                    />
                                </div>
                                <button
                                    type="submit"
                                    className="w-full bg-cyan-500 text-white py-3 rounded-lg font-semibold hover:bg-cyan-600 transition-colors text-sm sm:text-base disabled:opacity-60"
                                    disabled={loading}
                                >
                                    {loading ? 'Sending...' : 'Reset Password'}
                                </button>
                            </form>

                            <div className="mt-4 text-center">
                                <Link
                                    to="/signin"
                                    className="text-cyan-400 hover:text-cyan-300 text-sm sm:text-base"
                                >
                                    Back to Sign In
                                </Link>
                            </div>
                        </>
                    )}
                </div>
            </main>

            {/* Footer */}
            <footer className="py-6 sm:py-8 bg-gray-800 mt-auto">
                <div className="container mx-auto px-4 sm:px-6">
                    <div className="flex flex-col sm:flex-row justify-between items-center gap-2 sm:gap-0">
                        <div className="text-gray-400 mb-2 sm:mb-0 text-center sm:text-left text-xs sm:text-base">
                            © {new Date().getFullYear()} Kifayti Retail Private Limited. All rights reserved.
                        </div>
                        <div className="flex flex-col sm:flex-row gap-2 sm:gap-6 items-center">
                            <Link to="/terms" className="text-gray-400 hover:text-cyan-400">
                                Terms & Conditions
                            </Link>
                            <Link to="/privacy" className="text-gray-400 hover:text-cyan-400">
                                Privacy Policy
                            </Link>
                        </div>
                    </div>
                </div>
            </footer>
        </div>
    );
};

export default ForgotPassword; 